import React, { FC } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { formatNumber } from '@/shared/lib/number';

import styles from './Rating.module.css';
import { KpLogo } from './KpLogo';
import { presentRatingValueType } from './presentRatingValueType';
import { presentRatingValue } from './presentRatingValue';

type RatingVariant = 'default' | 'primary';

type RatingValueSize = 'default' | 'large';

export type RatingValueType = 'positive' | 'neutral' | 'negative' | 'unknown';

const RATING_TYPE_CLASS_MAP: Record<RatingValueType, string> = {
    negative: styles.ratingValue_bad,
    neutral: styles.ratingValue_normal,
    positive: styles.ratingValue_good,
    unknown: styles.ratingValue_unknown,
};

interface RatingProps {
    id?: string;
    dataTid?: string;
    className?: string;
    value: number | null;
    votesCount?: number;
    showLogo?: boolean;
    type?: RatingVariant;
    size?: RatingValueSize;
}

export const Rating: FC<RatingProps> = ({
    id,
    dataTid,
    className,
    value,
    votesCount,
    showLogo = true,
    size = 'default',
    type = 'default',
}) => {
    const intl = useIntl();

    const ratingType = presentRatingValueType({ value });
    const numericValue = presentRatingValue({ value, fractionDigits: 1 });
    const formattedVotesCount = formatNumber(votesCount);

    let ariaLabel: string = '';
    if (value === null) {
        ariaLabel = intl.formatMessage({
            id: 'allplay.ratingUnknown',
            defaultMessage: 'Rating not available',
            description: 'Рейтинг недоступен',
        });
    }

    if (votesCount !== undefined && votesCount !== null) {
        ariaLabel = intl.formatMessage(
            {
                id: 'allplay.ratingFullLabel',
                defaultMessage: 'Rating {value} from {count} votes',
                description: 'Лейбл для рейтинга фильма с количеством оценок',
            },
            {
                value: numericValue,
                count: votesCount,
            }
        );
    }

    ariaLabel = intl.formatMessage(
        {
            id: 'allplay.ratingShortLabel',
            defaultMessage: 'Rating {value}',
            description: 'Лейбл для рейтинга фильма без количества оценок',
        },
        { value: numericValue }
    );

    return (
        <div
            id={id}
            className={classnames(
                styles.rating,
                { [styles[`rating_${size}`]]: size !== 'default' },
                className
            )}
            role="img"
            aria-label={ariaLabel}
            data-tid={dataTid}
        >
            <div
                className={classnames(
                    styles.ratingValue,
                    { [styles[`ratingValue_${type}`]]: type !== 'default' },
                    RATING_TYPE_CLASS_MAP[ratingType]
                )}
                aria-hidden="true"
            >
                {showLogo && <KpLogo className={styles.logo} aria-hidden="true" />}
                <span>{numericValue}</span>
            </div>
            {votesCount !== undefined && votesCount !== null && (
                <span
                    className={classnames(
                        styles.ratingVotesCount,
                        styles.ratingVotesCount_marginLeft
                    )}
                    aria-hidden="true"
                >
                    {formattedVotesCount}
                </span>
            )}
        </div>
    );
};
